import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import AnimatedBackground from './AnimatedBackground';
import heroVideo from '../assets/video/Hero_video.mp4';
import { useTranslation } from '../hooks/useTranslation';

const Hero = () => {
  const { t } = useTranslation();
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    
    if (video) {
      // Когда видео заканчивается, перематываем на 1 секунду назад и ставим на паузу
      const handleVideoEnd = () => {
        video.currentTime = Math.max(0, video.duration - 1); // Перемотка на 1 сек назад
        video.pause();
      };
      
      video.addEventListener('ended', handleVideoEnd);
      
      return () => {
        video.removeEventListener('ended', handleVideoEnd);
      };
    }
  }, []);

  // Отслеживание видимости секции
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
        
        const video = videoRef.current;
        if (video && entry.isIntersecting) {
          // Когда секция видна - запускаем видео с начала
          video.currentTime = 0;
          video.play().catch(() => {
            // Video play failed - silently handle
          });
        }
      },
      {
        threshold: 0.5, // Запускать когда 50% секции видно
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gray-50"
    >
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        {/* Animated fallback background */}
        <AnimatedBackground />
        
        {/* Construction Materials Video Overlay */}
        <div className="relative w-full h-full">
          <video
            ref={videoRef}
            muted
            playsInline
            className={`w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoadedData={() => setVideoLoaded(true)}
            onError={() => setVideoLoaded(false)}
          >
            {/* Custom PROBUILD MARKET hero video */}
            <source src={heroVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
        <div className="space-y-6">
          <h1 className="text-5xl md:text-7xl font-light tracking-tight leading-tight animate-fade-in-up">
            {t('hero.title')}
            <span className="block font-normal mt-2">{t('hero.titleTogether')}</span>
          </h1>
          
          <p className="text-xl md:text-2xl font-light max-w-2xl mx-auto leading-relaxed opacity-90 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            {t('hero.subtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <Button
              size="lg"
              className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-3 text-lg font-medium hover:scale-105 transition-all duration-200"
              onClick={() => document.querySelector('#solutions')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('hero.exploreSolutions')}
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        </div>

       
      </div>

      
    </section>
  );
};

export default Hero;